import React, { useState, useEffect } from 'react';
import { IconButton, Menu, MenuItem, Box, Typography, Avatar, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import { logout } from '../services/authService';
import Loader from './Loader';

const ProfileDropdown = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    // Get user info from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUserInfo({
          name: parsedUser.name || parsedUser.username || 'User',
          email: parsedUser.email || 'user@example.com',
        });
      } catch (error) {
        // If parsing fails, try individual items
        const userName = localStorage.getItem('userName') || localStorage.getItem('username') || 'User';
        const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email') || 'user@example.com';
        setUserInfo({ name: userName, email: userEmail });
      }
    } else {
      // Fallback to individual localStorage items
      const userName = localStorage.getItem('userName') || localStorage.getItem('username') || 'User';
      const userEmail = localStorage.getItem('userEmail') || localStorage.getItem('email') || 'user@example.com';
      setUserInfo({ name: userName, email: userEmail });
    }
  }, []);

  const userInitial = userInfo.name.charAt(0).toUpperCase();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      handleClose();
      await logout();
      // Navigation handled by logout function
    } catch (error) {
      console.error('Logout error:', error);
      setIsLoggingOut(false);
    }
  };

  const handleCompanySettings = () => {
    handleClose();
    navigate('/company-settings');
  };

  // ✅ Add handler for profile settings
  const handleProfileSettings = () => {
    handleClose();
    navigate('/profile-settings');
  };

  const handleStoreUserSettings = () => {
    handleClose();
    navigate('/store-user-settings');
  };

  const menuItems = [
    {
      label: 'Profile Settings',
      icon: <SettingsIcon sx={{ fontSize: '1.2rem' }} />,
      action: handleProfileSettings, // ✅ Updated action
    },
    {
      label: 'Company Settings',
      icon: <BusinessIcon sx={{ fontSize: '1.2rem' }} />,
      action: handleCompanySettings,
    },
    {
      label: 'Store & User Setting',
      icon: <StoreIcon sx={{ fontSize: '1.2rem' }} />,
      action: handleStoreUserSettings,
    },
    {
      label: 'Logout',
      icon: <LogoutIcon sx={{ fontSize: '1.2rem' }} />,
      action: handleLogout,
      divider: true,
      danger: true,
    },
  ];

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          padding: 0,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
            color: '#667eea',
            fontWeight: 700,
            fontSize: '1.1rem',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            },
          }}
        >
          {userInitial}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            minWidth: 240,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
            border: '1px solid rgba(0,0,0,0.08)',
          },
        }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 2, py: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.3rem',
              }}
            >
              {userInitial}
            </Avatar>
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <Typography
                sx={{
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  color: '#2d3748',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {userInfo.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#718096',
                  lineHeight: 1.2,
                  mt: 0.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {userInfo.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

        {/* Menu Items */}
        {menuItems.map((item, index) => (
          <React.Fragment key={index}>
            {item.divider && <Divider sx={{ my: 0.5, borderColor: 'rgba(0,0,0,0.06)' }} />}
            <MenuItem
              onClick={item.action}
              sx={{
                py: 1.5,
                px: 2,
                mx: 0.5,
                my: 0.25,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: item.danger
                    ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.08) 100%)'
                    : 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Box
                sx={{
                  color: item.danger ? '#ef4444' : '#667eea',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {item.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  color: item.danger ? '#ef4444' : '#2d3748',
                }}
              >
                {item.label}
              </Typography>
            </MenuItem>
          </React.Fragment>
        ))}
      </Menu>
      <Loader open={isLoggingOut} />
    </>
  );
};

export default ProfileDropdown;